import type { TOC } from '@ember/component/template-only';
import {
  PrismBanner,
  PrismLoadingBar,
  PrismViewSwitcher,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import GeneralSmartInsightsDropdownPriorityFilter from 'lumen/components/general/smart-insights/dropdown-priority-filter';
import MarketSmartInsightsCalendarCalendarSmartInsights from 'lumen/components/market/smart-insights/calendar/calendar-smart-insights';
import MarketSmartInsightsColumnSmartInsights from 'lumen/components/market/smart-insights/column-smart-insights';
import MarketSmartInsightsDrawerSmartInsights from 'lumen/components/market/smart-insights/drawer-smart-insights';
import { PORTFOLIO_SMART_INSIGHTS_AGGREGATION_OPTIONS } from 'lumen/components/market/smart-insights/dropdown-aggregation';
import MarketSmartInsightsDropdownSort from 'lumen/components/market/smart-insights/dropdown-sort';
import MarketSmartInsightsDropdownsDropdownDormFilter from 'lumen/components/market/smart-insights/dropdowns/dropdown-dorm-filter';
import MarketSmartInsightsDropdownsDropdownPropertyFilter from 'lumen/components/market/smart-insights/dropdowns/dropdown-property-filter';
import MarketSmartInsightsDropdownsDropdownRegionalVpFilter from 'lumen/components/market/smart-insights/dropdowns/dropdown-regional-vp-filter';
import type MarketSmartInsightsController from 'lumen/controllers/market/smart-insights';
import { HEADER_HEIGHT } from 'lumen/utils/constants/general-constants';

interface MarketSmartInsightsSignature {
  Args: {
    controller: MarketSmartInsightsController;
    model: unknown;
  };
}

<template>
  <header class="header header--portfolio-smart-insights flex items-center">
    <div
      class="content-wrapper flex flex-row items-center justify-between gap-200"
    >
      <div class="flex flex-row gap-200">
        {{#if @controller.portfolioSmartInsights.regionalVps.length}}
          <MarketSmartInsightsDropdownsDropdownRegionalVpFilter
            @regionalVps={{@controller.portfolioSmartInsights.regionalVps}}
          />
        {{/if}}

        {{#if @controller.portfolioSmartInsights.dorms.length}}
          <MarketSmartInsightsDropdownsDropdownDormFilter
            @dorms={{@controller.portfolioSmartInsights.dorms}}
          />
        {{/if}}

        <MarketSmartInsightsDropdownsDropdownPropertyFilter
          @isLoading={{@controller.myHotelsLoading}}
          @selectedPropertyIds={{@controller.selectedPropertyIds}}
          @setSelectedPropertyIds={{@controller.setSelectedPropertyIds}}
        />

        <GeneralSmartInsightsDropdownPriorityFilter
          @selectedPriorities={{@controller.priorities}}
          @setSelectedPriorities={{@controller.setPrioritiesAndCloseDrawer}}
        />
      </div>

      <PrismViewSwitcher
        @activeButtonId={{@controller.groupBy}}
        @onSetActiveButtonId={{@controller.setGroupByAndCloseDrawer}}
        as |ViewSwitcher|
      >
        <ViewSwitcher.button
          @id={{PORTFOLIO_SMART_INSIGHTS_AGGREGATION_OPTIONS.STAY_DATE}}
          @label={{t "market.smart_insights.view_switcher.date"}}
        />

        <ViewSwitcher.button
          @id={{PORTFOLIO_SMART_INSIGHTS_AGGREGATION_OPTIONS.HOTEL}}
          @label={{t "market.smart_insights.view_switcher.property"}}
        />
      </PrismViewSwitcher>
    </div>
  </header>

  {{#if @controller.model.smartInsights.isRunning}}
    <PrismBanner
      @bannerMessage={{t
        @controller.bannerTranslationKey
        count=@controller.portfolioSmartInsights.currentHotelNumber
        maxCount=@controller.portfolioSmartInsights.maxFetchingHotelsCount
      }}
      @bannerStyle="intelligence"
      @hasCta={{false}}
      @isLoading={{true}}
      @position="top"
    />
  {{/if}}

  <div class="content-wrapper main-content relative flex h-full gap-400">
    {{#if @controller.model.myHotels.isRunning}}
      <PrismLoadingBar @isFixed={{true}} @positionTop={{HEADER_HEIGHT}} />
    {{/if}}

    {{#if @controller.isGroupByStayDate}}
      <MarketSmartInsightsCalendarCalendarSmartInsights
        @insightCountsByStayDate={{@controller.insightCountsByStayDate}}
        @insightsByStayDate={{@controller.insightsByStayDate}}
        @maxInsightEntriesPerDay={{@controller.maxInsightEntriesPerDay}}
        @selectSmartInsightsDay={{@controller.goToSmartInsightDay}}
      />
    {{/if}}

    <div class="flex h-full min-w-0 flex-col gap-300">
      {{#unless @controller.isGroupByStayDate}}
        <MarketSmartInsightsDropdownSort
          @setSortBy={{@controller.setSortByAndCloseDrawer}}
          @sortBy={{@controller.sortBy}}
        />
      {{/unless}}

      <div
        class="rounded-200 bg-neutral-subdued flex h-full overflow-x-auto overflow-y-hidden shadow-neutral-100"
      >
        {{#each @controller.columns as |column|}}
          <MarketSmartInsightsColumnSmartInsights
            @groupedInsights={{@controller.getInsightEntriesForColumn
              column.id
            }}
            @hasFetchedData={{@controller.getHasFetchedDataForColumn column.id}}
            @id={{column.id}}
            @isGroupByStayDate={{@controller.isGroupByStayDate}}
            @isLoading={{or
              @controller.model.smartInsights.isRunning
              @controller.model.myHotels.isRunning
            }}
            @onHotelInsightsCardClick={{@controller.handleHotelInsightsCardClick}}
            @sortBy={{@controller.sortBy}}
            @title={{column.title}}
          />
        {{/each}}
      </div>
    </div>
  </div>

  <MarketSmartInsightsDrawerSmartInsights
    @collapseDrawer={{@controller.collapseSmartInsightsDrawer}}
    @isOpen={{@controller.showSmartInsightsSidePanel}}
    @onArchive={{@controller.archiveInsight}}
    @positionTop={{HEADER_HEIGHT}}
    @setSmartInsightsTab={{@controller.setSmartInsightsTab}}
    @smartInsightDay={{@controller.selectedSmartInsightDay}}
    @smartInsightsTab={{@controller.smartInsightsTab}}
  />
</template> satisfies TOC<MarketSmartInsightsSignature>;
